import React, { useState, useEffect } from 'react';
import { businessImageUpload, deleteBusinessImage, getBusinessImages, uploadBusinessImage } from '../../../../services/businessService';
import { toast } from "react-toastify";
import "../../../../css/business-images.css";
import { getImage } from '../../../../utils/cloudinarySizing';
import { resizeImagePreview } from '../../../../utils/imageUtils';

const MyImages = ({ id }) => {
    const [photos, setPhotos] = useState([]);
    const [isLoading, setLoading] = useState(true);
    const [isUploading, setUploading] = useState(false);
    const [selectedPhoto, setSelectedPhoto] = useState(null);
    const [fullResSrc, setFullResSrc] = useState(null);

    const MAX_UPLOAD_BATCH = 12;
    

    // --- Fetch photos ---
    const fetchPhotos = async () => {
        try {
            const response = await getBusinessImages(id);
            setPhotos(response.images || []); // <-- ensure it's an array
        } catch (error) {
            console.error("Error fetching photos:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPhotos();
    }, []);

    // --- Upload photos ---
    const handlePhotoUpload = async (event) => {

        const files = Array.from(event.target.files);

        // 🔒 PER-UPLOAD LIMIT ONLY
        if (files.length > MAX_UPLOAD_BATCH) {
            toast.error(
                `You can upload up to ${MAX_UPLOAD_BATCH} images at a time.`
            );
            event.target.value = "";
            return;
        }

        const previewPromises = files.map(
            (file) =>
                new Promise((resolve) => {
                    resizeImagePreview(file, (previewSrc) => {
                        resolve({
                            id: `temp-${Math.random()}`,
                            image: previewSrc,
                            description: "",
                            isTemp: true,
                        });
                    });
                })
        );
        const tempPreviews = await Promise.all(previewPromises);


        // Instantly add previews (no layout jump)
        setPhotos((prev) => [...tempPreviews, ...prev]);

        const formData = new FormData();
        files.forEach((file) => formData.append("image", file));

        try {
            setUploading(true);

            const response = await uploadBusinessImage(formData);
            const newImages = Array.isArray(response) ? response : [response];

            setPhotos((prev) => {
                const withoutTemps = prev.filter((p) => !p.isTemp);

                // backend already returns correct order
                return [...newImages, ...withoutTemps];
            });

            toast.success("Images uploaded!");
        } catch (error) {
            console.error("Upload failed:", error);
            toast.error("Upload failed");
        } finally {
            setUploading(false);
        }
    };

    // --- Delete photo ---
    const handleDelete = async (photoId) => {
        if (!window.confirm("Delete this image?")) return;

        try {
            await deleteBusinessImage(photoId);
            setPhotos((prev) => prev.filter((p) => p.id !== photoId));
            toast.success("Image deleted");
        } catch (error) {
            console.error("Delete failed:", error);
            toast.error("Failed to delete");
        }
    };

    // --- Click photo to enlarge ---
    const handleClickPhoto = (photo) => {
        setSelectedPhoto(photo);
        setFullResSrc(photo.image_800); // initial preview

        // Lazy load original full-res
        if (photo.image) {
            const img = new Image();
            img.src = photo.image;
            img.onload = () => setFullResSrc(photo.image);
        }
    };

    if (isLoading) return null;

    return (
        <div className="business-images-container">
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                }}
            >
                <div className="add-photo-box">
                    <div
                        className="add-photo-button"
                        onClick={() => document.getElementById("photo-upload-input").click()}
                    >
                        + Upload
                    </div>

                    <input
                        id="photo-upload-input"
                        type="file"
                        accept="image/*"
                        multiple
                        style={{ display: "none" }}
                        onChange={handlePhotoUpload}
                    />
                </div>
                <p style={{ color: "var(--text)", margin: 0 }}>
                    {photos.length} {photos.length === 1 ? "Image" : "Images"}
                </p>
            </div>

            <div className="photo-container">
                {photos.map((photo, index) => (
                    <div className="photo-box" key={index}>
                        {!photo.isTemp && (
                            <div className="delete-icon" onClick={() => handleDelete(photo.id)}>
                                <i className="fas fa-trash job-delete"></i>
                            </div>
                        )}

                        <img
                            src={getImage(photo.image, "job_sm")}
                            alt={`Photo ${index + 1}`}
                            onClick={() => handleClickPhoto(photo)}
                            style={{ cursor: "pointer" }}
                        />
                        {photo.description && <p>{photo.description}</p>}
                    </div>
                ))}
            </div>

            {selectedPhoto && (
                <div className="lightbox-backdrop" onClick={() => setSelectedPhoto(null)}>
                    <img
                        src={selectedPhoto.image}
                        alt="Full Size"
                        className="lightbox-image"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}
        </div>
    );
};

export default MyImages;